import { chargeableLeaveDates } from '../services/leave-calendar';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import moment from 'moment';
import { BusinessServices } from 'src/app/kuba/businesses/services/business.services';
import { EmployeeLeaveService } from '../services/employeeleave.service';
import { EmployeeServices } from '../services/employee.services';
import { BaseServices } from './../../kuba.services';
import { Status } from 'src/app/_models';

@Component({
  selector: 'leave-list',
  templateUrl: 'employee-leaveList.component.html',
  styleUrls: ['employee-leaveList.component.scss'],
  standalone: false
})
export class LeaveListComponent implements OnInit {
  private readonly notifications = inject(NotificationService);
  private subscriptions: Subscription[] = [];
  leaveListForm: FormGroup;
  DayPilotForm: FormGroup;
  statusList: SelectItem[] = [];
  approvalPersonList: SelectItem[] = [];
  additionalData: any;
  dayPilotLeaveList: any[] = [];
  // Open applications ignore the calendar range; the period list mirrors the calendar.
  pendingLeaves: any[] = [];
  periodLeaves: any[] = [];
  listTab: 'pending' | 'period' = 'pending';
  resourcesList: any[] = [];
  eventsList: any[] = [];
  events: Date[] = [];
  groupedlist: Date[][] = [];
  allEmployees: any[] = [];
  holidayList: any[] = [];
  modifiedEmployeeLeaves: any[] = [];
  currentEvent: any;
  displayMenu = false;
  fullscreen = false;
  loading = false;
  dayPilotStartDate = '';
  dayPilotEndaDate = '';
  getYear: number;
  locale = this.translate.currentLang() || 'en';
  constructor(
    public employeeLeaveService: EmployeeLeaveService,
    private employeeService: EmployeeServices,
    private route: ActivatedRoute,
    private _fb: FormBuilder,
    private translate: TranslateService,
    private confirmationService: ConfirmationService,
    private businessservice: BusinessServices
  ) {
    this.leaveListForm = this._fb.group({
      startDate: [''],
      endDate: ['']
    });
    this.DayPilotForm = this._fb.group({
      DayPilotStartDate: [''],
      DayPilotEndDate: ['']
    });

    this.subscriptions.push(
      this.translate.stream('FILENAME').subscribe(val => {
        this.additionalData = {
          fileName: val.EMPLOYEE_LEAVE_LIST,
          header: 'Employee Leave List',
          businessId: BaseServices.BusinessId,
          cultureInfo: BaseServices.userCultureInfo(),
          columnNames: [
            { title: 'Employee Name', dataKey: 'EmployeeName' },
            { title: 'Start Date', dataKey: 'StartDate' },
            { title: 'End Date', dataKey: 'EndDate' },
            { title: 'No Of Days', dataKey: 'NoOfDays' },
            { title: 'Status', dataKey: 'StatusText' },
            { title: 'Approval Person', dataKey: 'ApprovalPersonName' },
            { title: 'Date Approval', dataKey: 'AprDate' }
          ]
        };
      })
    );
  }

  ngOnInit() {
    this.dayPilotLeaveList = this.route.snapshot.data['list'] ?? [];
    this.getYear = new Date().getFullYear();
    this.setRange(
      moment().startOf('month').toDate(),
      moment().endOf('month').toDate()
    );
    this.bindDropdowns();
    // Land on the pending approvals only when there is something to decide.
    this.listTab = this.pendingLeaves.length > 0 ? 'pending' : 'period';
    this.subscriptions.push(
      this.employeeService
        .getActiveEmployees(BaseServices.BusinessId)
        .subscribe(employees => {
          this.allEmployees = Array.isArray(employees) ? employees : [];
          this.renderTimeline();
        }),
      this.employeeLeaveService
        .getAllHolidays(BaseServices.BusinessId)
        .subscribe(holidays => {
          this.holidayList = Array.isArray(holidays) ? holidays : [];
        }),
      this.translate.onLangChange.subscribe(event => {
        this.locale = event.lang;
      })
    );
  }

  // Leave dates are calendar dates. Avoid UTC conversion and elapsed-hour math at DST boundaries.
  calendarDate(value: string | Date) {
    return value instanceof Date
      ? moment(value).startOf('day')
      : moment(String(value).slice(0, 10), ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  }

  setRange(start: Date, end: Date) {
    this.DayPilotForm.setValue({
      DayPilotStartDate: start,
      DayPilotEndDate: end
    });
    this.dayPilotStartDate = this.calendarDate(start).format('YYYY-MM-DD');
    this.dayPilotEndaDate = this.calendarDate(end).format('YYYY-MM-DD');
    this.getYear = start.getFullYear();
    this.renderTimeline();
  }

  Search() {
    const { DayPilotStartDate: start, DayPilotEndDate: end } =
      this.DayPilotForm.value;
    if (!(start instanceof Date) || !this.calendarDate(start).isValid()) {
      this.notifications.warn('FROM_DATE_ERR');
      return;
    }
    if (!(end instanceof Date) || !this.calendarDate(end).isValid()) {
      this.notifications.warn('END_DATE_ERR');
      return;
    }
    const days = this.calendarDate(end).diff(this.calendarDate(start), 'days');
    if (days < 0 || days > 731) {
      this.notifications.warn(
        days < 0 ? 'FROM_END_DATE_INVALID_ERR' : 'FROM_END_DATE_EXCEED_ERR'
      );
      return;
    }
    this.setRange(start, end);
    this.reloadLeaves();
  }

  changeDayPilotHeader(type: number) {
    const unit = type === 1 ? 'isoWeek' : type === 2 ? 'month' : 'year';
    const anchor = this.calendarDate(this.dayPilotStartDate);
    this.setRange(
      anchor.clone().startOf(unit).toDate(),
      anchor.clone().endOf(unit).toDate()
    );
    this.reloadLeaves();
  }

  moveRange(direction: number) {
    const start = this.calendarDate(this.dayPilotStartDate);
    const end = this.calendarDate(this.dayPilotEndaDate);
    const days = end.diff(start, 'days') + 1;
    if (
      start.isSame(start.clone().startOf('year')) &&
      end.isSame(end.clone().endOf('year'), 'day')
    ) {
      this.setRange(
        start.add(direction, 'years').toDate(),
        end.add(direction, 'years').endOf('year').toDate()
      );
    } else if (
      start.date() === 1 &&
      end.isSame(start.clone().endOf('month'), 'day')
    ) {
      this.setRange(
        start.add(direction, 'months').toDate(),
        start.clone().endOf('month').toDate()
      );
    } else {
      this.setRange(
        start.add(direction * days, 'days').toDate(),
        end.add(direction * days, 'days').toDate()
      );
    }
    this.reloadLeaves();
  }

  reloadLeaves() {
    this.loading = true;
    this.DayPilotForm.disable({ emitEvent: false });
    this.subscriptions.push(
      this.employeeLeaveService
        .getAllByBusinessId(BaseServices.BusinessId, this.getYear)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.DayPilotForm.enable({ emitEvent: false });
          })
        )
        .subscribe(result => {
          this.dayPilotLeaveList = Array.isArray(result) ? result : [];
          // Preserve unsaved timeline edits when changing the visible period.
          for (const change of this.modifiedEmployeeLeaves) {
            const leave = this.dayPilotLeaveList.find(
              item => item.Id === change.id
            );
            if (leave !== undefined)
              this.applyDraft(
                leave,
                change.startDate,
                change.endDate,
                change.status,
                change.noOfDays
              );
          }
          this.renderTimeline();
        })
    );
  }

  renderTimeline() {
    const start = this.calendarDate(this.dayPilotStartDate);
    const end = this.calendarDate(this.dayPilotEndaDate);
    this.events = [];
    this.groupedlist = [];
    if (
      !start.isValid() ||
      !end.isValid() ||
      end.diff(start, 'days') < 0 ||
      end.diff(start, 'days') > 731
    )
      return;
    for (
      const day = start.clone();
      day.isSameOrBefore(end);
      day.add(1, 'day')
    ) {
      this.events.push(day.toDate());
      const group = this.groupedlist[this.groupedlist.length - 1];
      if (
        this.groupedlist.length > 0 &&
        this.calendarDate(group[0]).isSame(day, 'month')
      )
        group.push(day.toDate());
      else this.groupedlist.push([day.toDate()]);
    }
    this.eventsList = this.dayPilotLeaveList.filter(
      leave =>
        this.calendarDate(
          leave.StartDate ?? leave.StartDateForFilter
        ).isSameOrBefore(end) &&
        this.calendarDate(
          leave.EndDate ?? leave.EndDateForFilter
        ).isSameOrAfter(start)
    );
    this.pendingLeaves = this.dayPilotLeaveList.filter(
      leave => leave.Status === Status.Open
    );
    this.periodLeaves = [...this.eventsList];
    const employees = new Map();
    // Balances come from the API for the displayed allocation year, even when a leave is off screen.
    for (const leave of this.dayPilotLeaveList) {
      if (!employees.has(leave.EmployeeId))
        employees.set(leave.EmployeeId, {
          id: leave.EmployeeId,
          name: leave.EmployeeName,
          days: leave.TotalDays,
          remainingDays: leave.RemainingDays,
          profileImg: leave.ProfileImage
        });
    }
    for (const employee of this.allEmployees) {
      if (!employees.has(employee.Id))
        employees.set(employee.Id, {
          id: employee.Id,
          name: employee.Name,
          days: null,
          remainingDays: null,
          profileImg: employee.Avatar
        });
    }
    this.resourcesList = Array.from(employees.values()).sort((a, b) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), this.locale)
    );
  }

  leaveLanes(employeeId: number): any[][] {
    const lanes: any[][] = [];
    const leaves = this.eventsList
      .filter(leave => leave.EmployeeId === employeeId)
      .sort((a, b) =>
        this.calendarDate(a.StartDate).diff(this.calendarDate(b.StartDate))
      );
    for (const leave of leaves) {
      const lane = lanes.findIndex(items =>
        this.calendarDate(items[items.length - 1].EndDate).isBefore(
          this.calendarDate(leave.StartDate)
        )
      );
      if (lane >= 0) lanes[lane].push(leave);
      else lanes.push([leave]);
    }
    return lanes;
  }

  leaveLeft(leave: any): number {
    return (
      (Math.max(
        0,
        this.calendarDate(leave.StartDate).diff(
          this.calendarDate(this.dayPilotStartDate),
          'days'
        )
      ) /
        this.events.length) *
      100
    );
  }

  leaveWidth(leave: any): number {
    const start = moment.max(
      this.calendarDate(leave.StartDate),
      this.calendarDate(this.dayPilotStartDate)
    );
    const end = moment.min(
      this.calendarDate(leave.EndDate),
      this.calendarDate(this.dayPilotEndaDate)
    );
    return (
      (Math.max(0, end.diff(start, 'days') + 1) / this.events.length) * 100
    );
  }

  dateLabel(date: string | Date, options: Intl.DateTimeFormatOptions): string {
    const locale = this.locale === 'no' ? 'nb' : this.locale;
    return new Intl.DateTimeFormat(locale || 'en', options).format(
      this.calendarDate(date).toDate()
    );
  }

  statusKey(status: number): string {
    return status === Status.Approved
      ? 'APPROVED'
      : status === Status.Open
        ? 'OPEN'
        : 'REJECTED';
  }

  leaveTooltip(leave: any): string {
    return [
      leave.EmployeeName,
      `${this.translate.instant('FROM_DATE')}: ${this.calendarDate(leave.StartDate).format('DD/MM/YYYY')}`,
      `${this.translate.instant('END_DATE')}: ${this.calendarDate(leave.EndDate).format('DD/MM/YYYY')}`,
      `${this.translate.instant('NO_OF_DAYS')}: ${leave.NoOfDays}`,
      `${this.translate.instant('STATUS')}: ${this.translate.instant(this.statusKey(leave.Status))}`,
      `${this.translate.instant('DESCRIPTION')}: ${leave.Description ?? '—'}`
    ].join('\n');
  }

  trackDate(_index: number, date: Date): number {
    return date.getTime();
  }
  trackIndex(index: number): number {
    return index;
  }

  trackLeave(_index: number, leave: any): number {
    return leave.Id;
  }

  onAvatarError(event: Event) {
    const image = event.target as HTMLImageElement;
    image.onerror = null;
    image.src = 'assets/icons/default_profile.jpg';
  }

  bindDropdowns() {
    this.subscriptions.push(
      this.translate.stream('SELECT_STATUSESFORLEAVE').subscribe(values => {
        this.statusList = [
          { label: values.SELECT, value: null },
          { label: values.OPEN, value: Status.Open },
          { label: values.APPROVED, value: Status.Approved },
          { label: values.REJECTED, value: Status.Rejected }
        ];
      })
    );
    this.approvalPersonList = Array.from(
      new Set<string>(
        this.dayPilotLeaveList.flatMap(
          leave => leave.ApprovalPersonNameList ?? []
        )
      )
    )
      .sort()
      .map(name => ({ label: name, value: name }));
  }

  selectLeave(leave: any) {
    this.currentEvent = leave;
    this.displayMenu = true;
  }

  changeLeaveStatus(status: number) {
    const leave = this.currentEvent;
    if (!leave || this.loading) return;
    const manager = [1, 2, 3].includes(+BaseServices.roleId);
    const key = this.statusKey(status);
    if (leave.Status === status) {
      this.notifications.warn(`${key}_ERR_MSG_2`);
      return;
    }
    if (
      leave.Status === Status.Approved &&
      status !== Status.Approved &&
      !manager
    ) {
      this.notifications.warn(`${key}_ERR_MSG_1`);
      return;
    }
    if (
      +leave.ApprovalPerson !== BaseServices.UserId &&
      !(leave.ApprovalPersonList ?? []).some(
        id => +id === BaseServices.UserId
      ) &&
      !manager &&
      !BaseServices.checkUserRights(11, BaseServices.UserRole)
    ) {
      this.notifications.warn(`${key}_ERR_MSG_3`);
      return;
    }
    this.confirmationService.confirm({
      message: this.translate.instant(`CONFIRM_LEAVE_${key}`),
      accept: () => {
        this.stageLeave(
          leave,
          leave.StartDate,
          leave.EndDate,
          status,
          leave.NoOfDays
        );
        this.displayMenu = false;
      }
    });
  }

  beginLeaveDrag(event: DragEvent, leave: any, resize = false) {
    if (leave.Status !== Status.Open || this.loading) {
      event.preventDefault();
      return;
    }
    event.stopPropagation();
    const track = (event.currentTarget as HTMLElement).closest(
      '.leave-track'
    ) as HTMLElement;
    const dayWidth = track.getBoundingClientRect().width / this.events.length;
    const day = Math.floor(
      (event.clientX - track.getBoundingClientRect().left) / dayWidth
    );
    const offset =
      day -
      this.calendarDate(leave.StartDate).diff(
        this.calendarDate(this.dayPilotStartDate),
        'days'
      );
    event.dataTransfer.setData(
      'application/x-kuba-leave',
      JSON.stringify([leave.Id, offset, resize])
    );
    event.dataTransfer.effectAllowed = 'move';
  }

  dropLeave(event: DragEvent, employeeId: number) {
    event.preventDefault();
    const payload = event.dataTransfer.getData('application/x-kuba-leave');
    if (!payload || this.loading) return;
    let data: unknown;
    try {
      data = JSON.parse(payload);
    } catch {
      return;
    }
    if (!Array.isArray(data) || data.length !== 3 || !Number.isFinite(data[1]))
      return;
    const leave = this.eventsList.find(
      item => item.Id === data[0] && item.EmployeeId === employeeId
    );
    if (leave === undefined || leave.Status !== Status.Open) return;
    const track = event.currentTarget as HTMLElement;
    const day = Math.max(
      0,
      Math.min(
        this.events.length - 1,
        Math.floor(
          (event.clientX - track.getBoundingClientRect().left) /
            (track.clientWidth / this.events.length)
        )
      )
    );
    const target = this.calendarDate(this.dayPilotStartDate).add(day, 'days');
    const start =
      data[2] === true
        ? this.calendarDate(leave.StartDate)
        : target.clone().subtract(data[1], 'days');
    const end =
      data[2] === true
        ? target
        : start
            .clone()
            .add(
              this.calendarDate(leave.EndDate).diff(
                this.calendarDate(leave.StartDate),
                'days'
              ),
              'days'
            );
    if (end.isBefore(start)) return;
    const days = chargeableLeaveDates(
      start.toDate(),
      end.toDate(),
      leave.IsShowWeekend === true || leave.IsShowWeekend === 'true',
      this.holidayList.map(item => item.HolidayDate)
    ).length;
    if (days === 0) return;
    this.stageLeave(
      leave,
      start.format('YYYY-MM-DD'),
      end.format('YYYY-MM-DD'),
      leave.Status,
      days
    );
  }

  private applyDraft(
    leave: any,
    start: string,
    end: string,
    status: number,
    days: number
  ) {
    leave.StartDate = leave.StartDateForFilter = start;
    leave.EndDate = leave.EndDateForFilter = end;
    leave.Status = status;
    leave.StatusText = this.statusKey(status);
    leave.NoOfDays = days;
  }

  private stageLeave(
    leave: any,
    start: string,
    end: string,
    status: number,
    days: number
  ) {
    this.applyDraft(leave, start, end, status, days);
    this.modifiedEmployeeLeaves = this.modifiedEmployeeLeaves.filter(
      change => change.id !== leave.Id
    );
    this.modifiedEmployeeLeaves.push({
      id: leave.Id,
      startDate: start,
      endDate: end,
      status,
      noOfDays: days
    });
    this.renderTimeline();
  }

  saveDayPilotEvents() {
    if (this.loading || this.modifiedEmployeeLeaves.length === 0) return;
    this.loading = true;
    this.DayPilotForm.disable({ emitEvent: false });
    this.subscriptions.push(
      this.employeeLeaveService
        .saveDayPilotLeaves(this.modifiedEmployeeLeaves, BaseServices.UserId)
        .subscribe({
          next: () => {
            this.modifiedEmployeeLeaves = [];
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.reloadLeaves();
          },
          error: () => {
            this.loading = false;
            this.DayPilotForm.enable({ emitEvent: false });
          }
        })
    );
  }

  exportLeaveDataToExcel() {
    this.subscriptions.push(
      this.employeeLeaveService
        .exportEmployeeLeaveToExcel(
          BaseServices.userLanguageId,
          BaseServices.BusinessId,
          `${this.dayPilotStartDate}T00:00:00`,
          `${this.dayPilotEndaDate}T00:00:00`
        )
        .subscribe(data => {
          const url = URL.createObjectURL(data as Blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `${this.translate.instant('EMPLOYEE_LEAVE_DATA')}-${BaseServices.BusinessName}.xlsx`;
          anchor.click();
          URL.revokeObjectURL(url);
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }
}
